/**
 * Single comment row inside a ReviewCard.
 * Canva-inspired: anchor chip + body text + hover-revealed action row + status badge.
 */

import { useState } from 'react';
import { MoreHorizontal, MessageSquarePlus, Trash2, X, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import type { OwnerReview, OwnerReviewComment, ReviewCommentStatus } from './types';
import { formatTimecode, hasRealTitle } from './format';

interface Props {
  review: OwnerReview;
  comment: OwnerReviewComment;
  /** True when this comment is sitting in the chat queue (chip is visible in composer). */
  isQueued: boolean;
  onSeek: (timestamp: number, slideId: string) => void;
  onAddToChat: () => void;
  onRemoveFromChat: () => void;
  onSetStatus: (status: ReviewCommentStatus) => void;
}

export function ReviewCommentRow({
  comment,
  onSeek,
  onAddToChat,
  onRemoveFromChat,
  onSetStatus,
  isQueued,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dismissed = comment.status === 'dismissed';
  const sent = comment.status === 'sent';

  return (
    <div
      className={cn(
        'group relative px-3 py-2.5 rounded-md transition-colors',
        'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
        dismissed && 'opacity-50',
      )}
    >
      {/* Anchor chip — Slide N · 0:14 (only show title when it's a real human-readable label) */}
      <button
        onClick={() => onSeek(comment.timestamp, comment.slideId)}
        className="inline-flex items-center gap-1.5 text-[11px] mb-1.5 px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 studio-text-secondary hover:studio-text-primary transition-colors"
        title="Jump to this moment in the player"
      >
        <span className="font-medium">Scene {comment.slideIndex + 1}</span>
        {hasRealTitle(comment.slideTitle, comment.slideId) && (
          <span className="opacity-70 max-w-[120px] truncate">· {comment.slideTitle}</span>
        )}
        <span className="font-mono tabular-nums opacity-80">· {formatTimecode(comment.timestamp)}</span>
      </button>

      {/* Body */}
      <p
        className={cn(
          'text-[13px] studio-text-primary whitespace-pre-wrap break-words leading-relaxed',
          dismissed && 'line-through',
        )}
      >
        {comment.text}
      </p>

      {/* Status row */}
      {(sent || isQueued) && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold">
          {sent && (
            <span className="inline-flex items-center gap-1 text-success">
              <Check className="h-3 w-3" /> Sent to Ivy
            </span>
          )}
          {isQueued && !sent && (
            <span className="inline-flex items-center gap-1 text-accent-foreground">
              <MessageSquarePlus className="h-3 w-3" /> Queued in chat
            </span>
          )}
        </div>
      )}

      {/* Action row — "Move back" stays visible (primary action in Queued view); */}
      {/* "Add to chat" is hover-only to keep the Open list scannable.            */}
      <div
        className={cn(
          'absolute right-2 top-2 flex items-center gap-0.5',
          isQueued || menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          'transition-opacity',
        )}
      >
        {!sent && !dismissed && (
          isQueued ? (
            <button
              onClick={onRemoveFromChat}
              title="Move back to Open"
              className="h-6 px-1.5 rounded text-[11px] inline-flex items-center gap-1 bg-accent hover:bg-accent/80 text-accent-foreground"
            >
              <X className="h-3 w-3" />
              Move back
            </button>
          ) : (
            <button
              onClick={onAddToChat}
              title="Add to Ivy chat"
              className="h-6 px-1.5 rounded text-[11px] inline-flex items-center gap-1 bg-black/[0.06] hover:bg-black/[0.12] dark:bg-white/[0.08] dark:hover:bg-white/[0.16] studio-text-primary"
            >
              <MessageSquarePlus className="h-3 w-3" />
              Add to chat
            </button>
          )
        )}
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="h-6 w-6 rounded inline-flex items-center justify-center hover:bg-black/[0.08] dark:hover:bg-white/[0.10] studio-text-secondary"
              aria-label="Comment actions"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {!sent && !dismissed && (
              <DropdownMenuItem onClick={() => onSetStatus('dismissed')} className="text-[12px]">
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Dismiss
              </DropdownMenuItem>
            )}
            {dismissed && (
              <DropdownMenuItem onClick={() => onSetStatus('pending')} className="text-[12px]">
                <Check className="h-3.5 w-3.5 mr-2" />
                Restore
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
